import React, { useState } from 'react';
import { useGlobalStorageContext } from '../contexts/GlobalStorageContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import './SettingsPage.css';

export const SettingsPage: React.FC = () => {
  const { colors, isDarkMode, themeMode, setThemeMode } = useTheme();
  const { language, setLanguage, t } = useLanguage();
  const { 
    manualLocation, 
    setLocation, 
    calcMethod, 
    setCalcMethod, 
    madhab, 
    setMadhab, 
    hijriAdjustment, 
    setHijriAdjustment,
    adhanEnabled,
    setAdhanEnabled,
    persistentNotifications,
    setPersistentNotifications,
    ishaFixedSettings,
    setIshaFixedSettings,
    fajrFixedSettings,
    setFajrFixedSettings
  } = useGlobalStorageContext();
  const [activeTab, setActiveTab] = useState<'appearance' | 'location' | 'prayers'>('appearance');
  const [city, setCity] = useState(manualLocation.city);
  const [lat, setLat] = useState(manualLocation.latitude.toString());
  const [lng, setLng] = useState(manualLocation.longitude.toString());
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const handleLocationUpdate = () => {
    setLocation(city, parseFloat(lat) || 60.1695, parseFloat(lng) || 24.9354);
  };

  const handleCitySearch = async () => {
    if (!searchQuery.trim()) return;
    setIsSearching(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(searchQuery)}&format=json&limit=5`
      );
      const data = await response.json();
      setSearchResults(data);
    } catch (error) {
      console.error('Search failed:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectCity = (result: any) => {
    const cityName = result.display_name.split(',')[0];
    setCity(cityName);
    setLat(result.lat);
    setLng(result.lon);
    setLocation(cityName, parseFloat(result.lat), parseFloat(result.lon));
    setSearchResults([]);
    setSearchQuery('');
  };

  const handleGPS = () => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          setLat(latitude.toString());
          setLng(longitude.toString());
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
            );
            const data = await response.json();
            const cityName = data.address.city || data.address.town || data.address.village || 'Location';
            setCity(cityName);
            setLocation(cityName, latitude, longitude);
          } catch {
            setLocation('Location', latitude, longitude);
          }
        },
        () => alert('Unable to get location')
      );
    }
  };

  return (
    <div className="settings-page" style={{ backgroundColor: colors.background, color: colors.text }}>
      <div className="settings-tabs" style={{ backgroundColor: colors.cardBackground, borderColor: colors.border }}>
        <button
          className={`settings-tab ${activeTab === 'appearance' ? 'active' : ''}`}
          onClick={() => setActiveTab('appearance')}
          style={{
            color: activeTab === 'appearance' ? colors.tint : colors.muted,
            borderBottomColor: activeTab === 'appearance' ? colors.tint : 'transparent'
          }}
        >
          {t.settings.appearance}
        </button>
        <button
          className={`settings-tab ${activeTab === 'location' ? 'active' : ''}`}
          onClick={() => setActiveTab('location')}
          style={{
            color: activeTab === 'location' ? colors.tint : colors.muted,
            borderBottomColor: activeTab === 'location' ? colors.tint : 'transparent'
          }}
        >
          {t.settings.location}
        </button>
        <button
          className={`settings-tab ${activeTab === 'prayers' ? 'active' : ''}`}
          onClick={() => setActiveTab('prayers')}
          style={{
            color: activeTab === 'prayers' ? colors.tint : colors.muted,
            borderBottomColor: activeTab === 'prayers' ? colors.tint : 'transparent'
          }}
        >
          {t.settings.prayers}
        </button>
      </div>

      <div className="settings-content">
        {activeTab === 'appearance' && (
          <div className="settings-section" style={{ backgroundColor: colors.cardBackground, borderColor: colors.border }}>
            <div className="setting-item">
              <label>{t.settings.lang}</label>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value as any)}
                style={{ backgroundColor: colors.background, color: colors.text, borderColor: colors.border }}
              >
                <option value="en">English</option>
                <option value="fi">Suomi</option>
                <option value="ar">العربية</option>
                <option value="es">Español</option>
                <option value="fr">Français</option>
                <option value="tr">Türkçe</option>
              </select>
            </div>
            <div className="setting-item">
              <label>{t.settings.theme}</label>
              <div className="theme-icons">
                <button
                  className={`theme-btn ${themeMode === 'system' ? 'active' : ''}`}
                  onClick={() => setThemeMode('system')}
                  style={{ backgroundColor: themeMode === 'system' ? colors.tint : colors.background }}
                  title={t.settings.system}
                >
                  📱
                </button>
                <button
                  className={`theme-btn ${themeMode === 'light' ? 'active' : ''}`}
                  onClick={() => setThemeMode('light')}
                  style={{ backgroundColor: themeMode === 'light' ? colors.tint : colors.background }}
                  title={t.settings.light}
                >
                  ☀️
                </button>
                <button
                  className={`theme-btn ${themeMode === 'dark' ? 'active' : ''}`}
                  onClick={() => setThemeMode('dark')}
                  style={{ backgroundColor: themeMode === 'dark' ? colors.tint : colors.background }}
                  title={t.settings.dark}
                >
                  🌙
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'location' && (
          <div className="settings-section" style={{ backgroundColor: colors.cardBackground, borderColor: colors.border }}>
            <div className="setting-item">
              <label>{'Search City'}</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleCitySearch()}
                  placeholder="Search for a city..."
                  style={{ backgroundColor: colors.background, color: colors.text, borderColor: colors.border, flex: 1 }}
                />
                <button onClick={handleCitySearch} className="btn-primary" style={{ backgroundColor: colors.tint }} disabled={isSearching}>
                  {isSearching ? '...' : '🔍'}
                </button>
              </div>
              {searchResults.length > 0 && (
                <div className="search-results" style={{ backgroundColor: colors.background, borderColor: colors.border, marginTop: '8px', maxHeight: '200px', overflowY: 'auto', border: '1px solid', borderRadius: '8px' }}>
                  {searchResults.map((result, index) => (
                    <div
                      key={index}
                      onClick={() => handleSelectCity(result)}
                      style={{
                        padding: '12px',
                        cursor: 'pointer',
                        borderBottom: index < searchResults.length - 1 ? `1px solid ${colors.border}` : 'none',
                        color: colors.text
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.border}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      {result.display_name}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="setting-item">
              <label>{t.settings.cityCountry}</label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                style={{ backgroundColor: colors.background, color: colors.text, borderColor: colors.border }}
              />
            </div>
            <div className="setting-item">
              <label>{t.settings.latitude}</label>
              <input
                type="number"
                value={lat}
                onChange={(e) => setLat(e.target.value)}
                style={{ backgroundColor: colors.background, color: colors.text, borderColor: colors.border }}
              />
            </div>
            <div className="setting-item">
              <label>{t.settings.longitude}</label>
              <input
                type="number"
                value={lng}
                onChange={(e) => setLng(e.target.value)}
                style={{ backgroundColor: colors.background, color: colors.text, borderColor: colors.border }}
              />
            </div>
            <div className="setting-actions">
              <button onClick={handleGPS} className="btn-secondary" style={{ borderColor: colors.border, color: colors.text }}>
                {t.settings.manual}
              </button>
              <button onClick={handleLocationUpdate} className="btn-primary" style={{ backgroundColor: colors.tint }}>
                {t.settings.save}
              </button>
            </div>
          </div>
        )}

        {activeTab === 'prayers' && (
          <div className="settings-section" style={{ backgroundColor: colors.cardBackground, borderColor: colors.border }}>
            <div className="setting-item">
              <label>Hijri Adjustment</label>
              <div className="hijri-buttons">
                {[-2, -1, 0, 1, 2].map((adj) => (
                  <button
                    key={adj}
                    className={`hijri-btn ${hijriAdjustment === adj ? 'active' : ''}`}
                    onClick={() => setHijriAdjustment(adj)}
                    style={{
                      backgroundColor: hijriAdjustment === adj ? colors.tint : colors.background,
                      color: hijriAdjustment === adj ? '#fff' : colors.text,
                      borderColor: colors.border
                    }}
                  >
                    {adj > 0 ? `+${adj}` : adj}
                  </button>
                ))}
              </div>
            </div>
            <div className="setting-item">
              <label>{t.settings.adhanEnabled}</label>
              <input
                type="checkbox"
                checked={adhanEnabled}
                onChange={(e) => setAdhanEnabled(e.target.checked)}
                className="toggle-checkbox"
              />
            </div>
            <div className="setting-item">
              <label>{t.settings.persistentNotifications}</label>
              <input
                type="checkbox"
                checked={persistentNotifications}
                onChange={(e) => setPersistentNotifications(e.target.checked)}
                className="toggle-checkbox"
              />
            </div>
            <div className="setting-item">
              <label>{t.settings.calcMethod}</label>
              <select
                value={calcMethod}
                onChange={(e) => setCalcMethod(e.target.value as any)}
                style={{ backgroundColor: colors.background, color: colors.text, borderColor: colors.border }}
              >
                <option value="MuslimWorldLeague">Muslim World League</option>
                <option value="Egyptian">Egyptian</option>
                <option value="Karachi">Karachi</option>
                <option value="UmmAlQura">Umm Al-Qura</option>
                <option value="Dubai">Dubai</option>
                <option value="MoonsightingCommittee">Moonsighting Committee</option>
                <option value="NorthAmerica">North America</option>
                <option value="Kuwait">Kuwait</option>
                <option value="Qatar">Qatar</option>
                <option value="Singapore">Singapore</option>
                <option value="Turkey">Turkey</option>
                <option value="Tehran">Tehran</option>
              </select>
            </div>
            <div className="setting-item">
              <label>{t.settings.asrMethod}</label>
              <select
                value={madhab}
                onChange={(e) => setMadhab(e.target.value as any)}
                style={{ backgroundColor: colors.background, color: colors.text, borderColor: colors.border }}
              >
                <option value="Shafi">{t.settings.standard}</option>
                <option value="Hanafi">Hanafi</option>
              </select>
            </div>
            <div className="setting-item">
              <label>{t.settings.ishaTime}</label>
              <select
                value={ishaFixedSettings.mode}
                onChange={(e) => setIshaFixedSettings({ ...ishaFixedSettings, mode: e.target.value as any })}
                style={{ backgroundColor: colors.background, color: colors.text, borderColor: colors.border }}
              >
                <option value="default">{t.settings.ishaDefault}</option>
                <option value="afterMaghrib">{t.settings.minAfterMaghrib}</option>
                <option value="fixedTime">{t.settings.fixedTime}</option>
              </select>
            </div>
            {ishaFixedSettings.mode === 'afterMaghrib' && (
              <div className="setting-item">
                <label>{t.settings.minAfterMaghrib}</label>
                <input
                  type="number"
                  value={ishaFixedSettings.minutesAfterMaghrib || 90}
                  onChange={(e) => setIshaFixedSettings({ ...ishaFixedSettings, minutesAfterMaghrib: parseInt(e.target.value) || 90 })}
                  style={{ backgroundColor: colors.background, color: colors.text, borderColor: colors.border }}
                />
              </div>
            )}
            {ishaFixedSettings.mode === 'fixedTime' && (
              <div className="setting-item">
                <label>Fixed Time (HH:MM)</label>
                <input
                  type="time"
                  value={ishaFixedSettings.fixedTime || '21:00'}
                  onChange={(e) => setIshaFixedSettings({ ...ishaFixedSettings, fixedTime: e.target.value })}
                  style={{ backgroundColor: colors.background, color: colors.text, borderColor: colors.border }}
                />
              </div>
            )}
            <div className="setting-item">
              <label>{t.settings.fajrTime}</label>
              <select
                value={fajrFixedSettings.mode}
                onChange={(e) => setFajrFixedSettings({ ...fajrFixedSettings, mode: e.target.value as any })}
                style={{ backgroundColor: colors.background, color: colors.text, borderColor: colors.border }}
              >
                <option value="default">{t.settings.fajrDefault}</option>
                <option value="beforeSunrise">{t.settings.minBeforeSunrise}</option>
                <option value="fixedTime">{t.settings.fixedTime}</option>
              </select>
            </div>
            {fajrFixedSettings.mode === 'beforeSunrise' && (
              <div className="setting-item">
                <label>{t.settings.minBeforeSunrise}</label>
                <input
                  type="number"
                  value={fajrFixedSettings.minutesBeforeSunrise || 90}
                  onChange={(e) => setFajrFixedSettings({ ...fajrFixedSettings, minutesBeforeSunrise: parseInt(e.target.value) || 90 })}
                  style={{ backgroundColor: colors.background, color: colors.text, borderColor: colors.border }}
                />
              </div>
            )}
            {fajrFixedSettings.mode === 'fixedTime' && (
              <div className="setting-item">
                <label>Fixed Time (HH:MM)</label>
                <input
                  type="time"
                  value={fajrFixedSettings.fixedTime || '05:00'}
                  onChange={(e) => setFajrFixedSettings({ ...fajrFixedSettings, fixedTime: e.target.value })}
                  style={{ backgroundColor: colors.background, color: colors.text, borderColor: colors.border }}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
